import { useQuery } from '@apollo/client';
import { faComment } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as R from 'ramda';
import { ApolloProviderV3 } from '~/apollo/client-v3';
import { graphql } from '~/apollo/generated/v3';
import {
  Role,
  type ReviewCommentParentType,
} from '~/apollo/generated/v3/graphql';
import { Modal } from '~/components/common/Modal';
import { Tooltip } from '~/components/common/Tooltip';
import { Button } from '~/components/ui/button';
import { useAuth } from '~/contexts/auth';
import { useModalState } from '~/hooks/modal';
import { createRefetchQueries } from '~/utils/graphql';
import { CreateReviewComment } from './create-review-comment-form';
import { ReviewCommentDisplay } from './review-comment-display';
import { NoItemsAlert } from '~/components/common/NoItemsAlert';

export const TARGET_REVIEW_COMMENTS = graphql(`
  query TargetReviewComments(
    $parentId: Int
    $parentType: ReviewCommentParentType
  ) {
    reviewCommentList(parentId: $parentId, parentType: $parentType) {
      ...reviewCommentParts
      author {
        ...publicUserParts
      }
    }
  }
`);

type Props = {
  parentType: ReviewCommentParentType;
  parentId: number;
  name: string;
};

export function TargetReviewCommentsModal(props: Props) {
  return (
    <ApolloProviderV3>
      <TargetReviewCommentsModalInner {...props} />
    </ApolloProviderV3>
  );
}

function TargetReviewCommentsModalInner({ parentType, parentId, name }: Props) {
  const { authority, hasAnyRole } = useAuth();

  const { show, showModal, hideModal } = useModalState();

  const { data } = useQuery(TARGET_REVIEW_COMMENTS, {
    variables: { parentType, parentId },
  });

  const refetchQueries = createRefetchQueries([
    {
      query: TARGET_REVIEW_COMMENTS,
      variables: { parentType, parentId },
    },
  ]);

  const reviewCommentList = R.sortBy(
    rc => rc.insertedAt,
    data?.reviewCommentList ?? [],
  );

  const hasComments = reviewCommentList.length > 0;

  // Users must be admin or in University of Aberdeen (20091) to view
  // https://github.com/omt-tech/safari/issues/2544
  const canViewReviewComments =
    hasAnyRole([Role.RoleAdmin]) || authority?.user.companyId === 20091;

  if (!canViewReviewComments) {
    return null;
  }

  return (
    <>
      <Tooltip
        message={`${reviewCommentList.length} review comment${
          reviewCommentList.length !== 1 ? 's' : ''
        }`}
      >
        <Button
          type="button"
          color="ghost"
          size="sm"
          onClick={showModal}
          className={hasComments ? 'text-error' : 'text-muted'}
        >
          <FontAwesomeIcon icon={faComment} />
        </Button>
      </Tooltip>

      <Modal open={show} onHide={hideModal}>
        <Modal.Body heading={`Review Comments for ${name}`}>
          <div className="space-y-4">
            {reviewCommentList.map(comment => (
              <ReviewCommentDisplay
                key={comment.id}
                reviewComment={comment}
                refetchQueries={refetchQueries}
              />
            ))}

            <NoItemsAlert
              show={!reviewCommentList.length}
              entity="review comments"
            />

            <CreateReviewComment
              parentId={parentId}
              parentType={parentType}
              name={name}
              refetchQueries={refetchQueries}
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
